import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useSessionStorage } from './useSessionStorage'

const REFRESH_THRESHOLD_SECONDS = 60 // Refresh the token if it's within 60 seconds of expiring

const readStoredToken = (): string => {
  const stored = sessionStorage.getItem('token')
  if (!stored) return ''
  try {
    return JSON.parse(stored) as string
  } catch {
    return ''
  }
}

const decodeJwtSegment = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padding = (4 - (base64.length % 4)) % 4
  return atob(base64 + '='.repeat(padding))
}

const readJwtExpiry = (token: string): number => {
  const [, payloadSegment] = token.split('.')
  if (!payloadSegment) throw new Error('Invalid token')
  const payload = JSON.parse(decodeJwtSegment(payloadSegment)) as { exp?: unknown }
  if (typeof payload.exp !== 'number') throw new Error('Token has no exp')
  return payload.exp
}

export const isTokenExpired = (providedToken?: string): boolean => {
  const token = providedToken || readStoredToken()
  if (!token) return true

  try {
    const now = Math.floor(Date.now() / 1000)
    return readJwtExpiry(token) < now
  } catch {
    toast.info('Decoding token failed. Treating token as expired.')
    return true
  }
}

// ✅ Memoized function to check if the token is near expiry
export const isTokenNearExpiry = (providedToken?: string): boolean => {
  const token = providedToken || readStoredToken()
  if (!token) return true

  try {
    const now = Math.floor(Date.now() / 1000)
    return readJwtExpiry(token) - now <= REFRESH_THRESHOLD_SECONDS
  } catch {
    toast.info('Token decoding failed. Attempting to refresh token...')
    return true
  }
}

export const useTokenValidator = () => {
  const [isRefreshing, setIsRefreshing] = useState(false)

  // ✅ Use the custom session storage hook
  const [storedToken, setStoredToken] = useSessionStorage('token', '')

  // ✅ Memoized function to check if the token is expired

  useEffect(() => {
    // ✅ Function to refresh the token
    const refreshAuthToken = async () => {
      if (isRefreshing) return

      setIsRefreshing(true)

      try {
        const response = await fetch('/rest/auth/refreshToken', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setStoredToken(data.token) // ✅ Use the hook to update the token
          toast.success('Token refreshed successfully.')
        } else {
          throw new Error('Failed to refresh token')
        }
      } catch {
        toast.error('Session expired. Please log in again.')
        sessionStorage.clear() // ✅ Clears all session data
        window.location.href = '/' // ✅ Redirect to home
      } finally {
        setIsRefreshing(false)
      }
    }

    // ✅ Function to validate the token
    const validateToken = () => {
      if (!storedToken || isTokenExpired()) {
        toast.error('Your session has expired. Please log in again.')
        sessionStorage.clear() // ✅ Clears all session data
        window.location.href = '/' // ✅ Redirect to home
      } else if (isTokenNearExpiry()) {
        refreshAuthToken()
      }
    }

    validateToken()
  }, [storedToken, isRefreshing, setStoredToken])

  return { isTokenExpired, isTokenNearExpiry }
}
